import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutUser } from '../utils/Axios';

const defaultMainItems = [
  { path: '/', label: 'Overview' },
  { path: '/crm/overview', label: 'iURM (CRM / Sales)' },
  { path: '/projects', label: 'iPROJECTS (Projects)' },
  { path: '/inventory', label: 'iMIM (Inventory / Ops)' },
  { path: '/finance', label: 'iFIM (Finance / Billing)' },
  { path: '/hr', label: 'iHIM (HR / People)' },
  { path: '/reports', label: 'Reports & Autonomy' }
];

const defaultAiItems = [
  { path: '/marketplace', label: 'AI Agent Marketplace' }
];

export default function Topbar() {
  const loc = useLocation();
  const navigate = useNavigate();
  const [pageTitle, setPageTitle] = useState('Overview');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const updateTitle = () => {
      const savedMain = localStorage.getItem('sidebar_main_items');
      const savedAi = localStorage.getItem('sidebar_ai_items');
      
      const mainItems = savedMain ? JSON.parse(savedMain) : defaultMainItems;
      const aiItems = savedAi ? JSON.parse(savedAi) : defaultAiItems;
      const allItems = [...mainItems, ...aiItems];

      let activeItem = allItems.find(i => {
        const isCrmRoute = i.path.startsWith('/crm') && loc.pathname.startsWith('/crm');
        const isActive = isCrmRoute || loc.pathname === i.path || (i.path !== '/' && loc.pathname.startsWith(i.path) && !i.path.startsWith('/crm'));
        return isActive;
      });

      if (activeItem) {
        setPageTitle(activeItem.label);
      } else {
        setPageTitle('Overview');
      }
    };

    updateTitle();

    window.addEventListener('sidebar_updated', updateTitle);
    return () => {
      window.removeEventListener('sidebar_updated', updateTitle);
    };
  }, [loc.pathname]);

  useEffect(() => {
    const rawUserData = sessionStorage.getItem('kt-user-data');
    if (rawUserData) {
      try {
        setUser(JSON.parse(rawUserData));
      } catch (e) {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/asknewa');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      localStorage.removeItem('asknewa_crm_panel_messages');
      localStorage.removeItem('newa_conversations');
      localStorage.removeItem('newa_last_active_conv_id');
      await logoutUser();
    } catch (e) {
      sessionStorage.removeItem('kt-auth-react-v');
      sessionStorage.removeItem('kt-user-data');
    }
    window.dispatchEvent(new Event('kt-auth-changed'));
    navigate('/login');
  };

  const displayName = user?.name || user?.username || user?.email || 'User Profile';
  const displayRole = user?.role || user?.designation || 'Account';
  const initial = displayName.charAt(0).toUpperCase();

  // Navigation items for mobile menu
  const getNavItems = () => {
    const savedMain = localStorage.getItem('sidebar_main_items');
    const savedAi = localStorage.getItem('sidebar_ai_items');
    const mainItems = savedMain ? JSON.parse(savedMain) : defaultMainItems;
    const aiItems = savedAi ? JSON.parse(savedAi) : defaultAiItems;
    return [...mainItems, ...aiItems];
  };

  const handleNavigation = (path) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  const isActiveRoute = (path) => {
    if (path === '/') return loc.pathname === '/';
    if (path.startsWith('/crm')) return loc.pathname.startsWith('/crm');
    return loc.pathname.startsWith(path);
  };

  return (
    <div className="topbar" style={{ 
      position: 'relative', 
      zIndex: 100,
      padding: isMobile ? '10px 16px' : '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #E5E7EB',
      gap: isMobile ? '8px' : '16px',
      flexWrap: isMobile ? 'wrap' : 'nowrap'
    }}>
      {/* Left Section */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '16px',
        flex: isMobile ? '1' : 'none',
        minWidth: isMobile ? '0' : 'auto',
        // marginLeft:isMobile?'10px':''
      }}>
        {/* Mobile Menu Toggle */}
       
        <div className="page-title" style={{
          fontSize: isMobile ? '16px' : '20px',
          fontWeight: '600',
          color: '#111827',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: isMobile ? '120px' : 'none'
        }}>
          {pageTitle}
        </div>
      </div>

      {/* Middle Section - Hidden on mobile */}
      {!isMobile && (
        <div className="chip" style={{
          padding: '6px 14px',
          backgroundColor: '#f3f4f6',
          borderRadius: '999px',
          fontSize: '12px',
          color: '#4B5563',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          border: '1px solid #E5E7EB'
        }}>
          This Month ▾
        </div>
      )}

      {/* Right Section */}
      <div className="topbar-right" style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '12px',
        position: 'relative',
        zIndex: 101,
        flexShrink: 0
      }}>
        {/* Notification Bell - Always visible */}
        <div className="chip" style={{
          padding: '6px 10px',
          cursor: 'pointer',
          fontSize: '16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '50%',
          width: isMobile ? '32px' : '36px',
          height: isMobile ? '32px' : '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          🔔
        </div>
        
        {/* Profile Button with Dropdown */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowProfileMenu(prev => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '4px' : '8px',
              backgroundColor: '#f5f6ff',
              border: '1px solid rgba(60, 52, 132, 0.18)',
              borderRadius: '999px',
              padding: isMobile ? '4px 8px 4px 4px' : '4px 10px 4px 6px',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#ebeeff')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f5f6ff')}
          >
            <div
              style={{
                width: isMobile ? '28px' : '24px',
                height: isMobile ? '28px' : '24px',
                borderRadius: '50%',
                backgroundColor: '#7C3AED',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: isMobile ? '12px' : '11px',
              }}
            >
              {initial}
            </div>
            {!isMobile && (
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#433478' }}>
                Profile ▾
              </span>
            )}
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.08)',
                border: '1px solid #E5E7EB',
                width: isMobile ? '200px' : '220px',
                zIndex: 9999,
                padding: '8px 0',
                textAlign: 'left',
                minWidth: isMobile ? '180px' : 'auto'
              }}
            >
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: isMobile ? '12px' : '13px', 
                  color: '#111827',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '160px' : '200px'
                }}>
                  {displayName}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '10px' : '11px', 
                  color: '#6B7280', 
                  marginTop: '2px', 
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '160px' : '200px'
                }}>
                  {displayRole}
                </div>
              </div>

              <div style={{ padding: '4px' }}>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: isMobile ? '8px 16px' : '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#DC2626',
                    fontSize: isMobile ? '13px' : '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#FEF2F2')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Navigation Menu Overlay */}
      {isMobile && showMobileMenu && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 9998,
            }}
            onClick={() => setShowMobileMenu(false)}
          />
          <div
            ref={mobileMenuRef}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: '280px',
              backgroundColor: '#ffffff',
              zIndex: 9999,
              padding: '20px 16px',
              overflowY: 'auto',
              boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
              transform: showMobileMenu ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.3s ease'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Menu
              </div>
              <button
                onClick={() => setShowMobileMenu(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6B7280'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {getNavItems().map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: isActiveRoute(item.path) ? '#f5f6ff' : 'transparent',
                    color: isActiveRoute(item.path) ? '#7C3AED' : '#374151',
                    fontWeight: isActiveRoute(item.path) ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '14px'
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}